import React, { useCallback, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { createLocale } from 'locales';

import Typography from 'components-new/typography/typography';
import Button from 'components-new/button/button';
import { showErrorToast, showSuccessToast } from 'components-new/toast/toast';

import { STATIC_FILES_URL } from 'constants/signup-flow-constants';

import type { Document } from 'state/__generated__/tadaahAPI';
import useGetFreelancerCertificates from 'state/remote/remote-freelancer/use-get-freelancer-certificates';

import FreelancerSignUpPageContainer from '../components/freelancer-sign-up-page-container';
import CertificatesInformationDialog from '../components/certificates-information-dialog';

import freelancerRoutes from '../freelancer-routes';

import CertificateList from './CertificateList';
import DeleteCertificateDialog from './DeleteCertificateDialog';

const localize = createLocale('freelancerCertificationInformationPageLocales');
const globaleLocalize = createLocale('global');

const CertificationInformation: React.FC = () => {
  const videoUrl = `${STATIC_FILES_URL}videos/signupflow/h8.mp4`;
  const videoPreviewImageURL = `${window.STATIC_URL}assets/signupflow/h8.png`;

  const [currentCertificate, setCurrentCertificate] = useState({} as Document);

  const [searchParams, setSearchParams] = useSearchParams({});

  const navigate = useNavigate();

  const { data: certificates } = useGetFreelancerCertificates();

  const closeDeleteCertificateDialog = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const navigateToDiplomaInfoPage = useCallback(() => {
    setSearchParams({});
    navigate(freelancerRoutes.diplomasInformation.path);
  }, [navigate, setSearchParams]);

  const goToAddCertificate = useCallback(() => {
    navigate(freelancerRoutes.addCertificationDocument.path);
  }, [navigate]);

  const goToFillInformation = useCallback(() => {
    navigate(freelancerRoutes.fillInformation.path);
  }, [navigate]);

  const handleDeleteCertificate = useCallback(
    (doc: Document) => {
      setCurrentCertificate(doc);
      setSearchParams({ confirmDeleteDiploma: 'true' });
    },
    [setSearchParams, setCurrentCertificate],
  );
  const shouldOpenDialog = !!(
    searchParams.get('confirmDeleteDiploma') &&
    searchParams.get('confirmDeleteDiploma') === 'true'
  );

  const onError = useCallback(
    (error: Error) => {
      showErrorToast({
        description: error.message,
        title: globaleLocalize('error'),
      });
      closeDeleteCertificateDialog();
    },
    [closeDeleteCertificateDialog],
  );

  const onSuccess = useCallback(() => {
    showSuccessToast({
      description: localize('certificateDeleted'),
      title: globaleLocalize('notification'),
    });
    closeDeleteCertificateDialog();
  }, [closeDeleteCertificateDialog]);

  return (
    <FreelancerSignUpPageContainer
      showGoBackButton
      onGoBackClick={navigateToDiplomaInfoPage}
      showHeader
      headerTitle={localize('title')}
      dialogInstructions={{
        component: <CertificatesInformationDialog />,
        shouldShowDialogInstructions: !certificates?.length,
      }}
      videoInstructions={{
        url: videoUrl,
        videoPreviewImageURL,
      }}
    >
      <Typography as="p" variant="body-medium" className="text-gray500 mt-6">
        {localize('helperMessage')}
      </Typography>

      <CertificateList handleDeleteCertificate={handleDeleteCertificate} />
      <Button
        label={localize('addCertificate')}
        variant="secondary"
        className="w-full mt-6 justify-center"
        onClick={goToAddCertificate}
      />
      <Button
        label={
          certificates && certificates.length > 0
            ? localize('continue')
            : localize('skipStep')
        }
        variant="primary"
        className="w-full mt-8 justify-center mb-10"
        onClick={goToFillInformation}
      />

      <DeleteCertificateDialog
        onDeleteError={onError}
        onDeleteSuccess={onSuccess}
        certificateName={currentCertificate.name || ''}
        isOpen={shouldOpenDialog}
        currentCertificateId={currentCertificate?.id?.toString() || ''}
        onCloseClicked={closeDeleteCertificateDialog}
      />
    </FreelancerSignUpPageContainer>
  );
};

export default CertificationInformation;
